// Cognito embedded auth — amazon-cognito-identity-js SDK (USER_SRP_AUTH flow).
// No redirect to the Cognito Hosted UI; authentication is fully embedded in the
// app's own UI so the user never leaves meds.eggblox.com.
//
// Environment (Vite, set by deploy.ps1):
//   VITE_COGNITO_USER_POOL_ID   e.g. us-east-1_xxxxxxxxx
//   VITE_COGNITO_CLIENT_ID      e.g. xxxxxxxxxxxxxxxxxx

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

const UP_ID     = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID    as string | undefined;

// Token storage keys — must match auth.ts constants exactly. Persisted in
// localStorage (see auth.ts header) so the session survives tab/app close.
const SK_ACCESS     = "cognitoAccessToken";
const SK_ID         = "cognitoIdToken";
const SK_REFRESH    = "cognitoRefreshToken";
const SK_EXPIRES_AT = "cognitoExpiresAt"; // epoch ms when access token expires

// ---- JWT decode helper (used by auth.ts) ----

/** Decode a JWT payload (base64url) WITHOUT verifying the signature. Safe for display only. */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1] ?? "";
    const padded  = payload + "===".slice(0, (4 - (payload.length % 4)) % 4);
    const json    = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// ---- Pool singleton ----

/** Check if Cognito env vars are configured (false in non-Cognito dev mode). */
export function isCognitoConfigured(): boolean {
  return Boolean(UP_ID && CLIENT_ID);
}

let _pool: CognitoUserPool | null = null;
function getPool(): CognitoUserPool {
  if (!_pool) {
    if (!UP_ID || !CLIENT_ID) throw new Error("Cognito env vars not set");
    _pool = new CognitoUserPool({ UserPoolId: UP_ID, ClientId: CLIENT_ID });
  }
  return _pool;
}

// ---- Token storage (mirrors auth.ts setCognitoTokens — no import to avoid circular dep) ----

function storeSession(session: CognitoUserSession): void {
  const expiration = session.getAccessToken().getExpiration(); // epoch seconds
  const nowSec     = Math.floor(Date.now() / 1000);
  localStorage.setItem(SK_ACCESS,     session.getAccessToken().getJwtToken());
  localStorage.setItem(SK_ID,         session.getIdToken().getJwtToken());
  localStorage.setItem(SK_REFRESH,    session.getRefreshToken().getToken());
  localStorage.setItem(SK_EXPIRES_AT, String(Date.now() + Math.max(0, expiration - nowSec) * 1000));
}

// ---- Auth result types ----

/** Discriminated union returned by signIn() / completeNewPassword(). */
export type SignInResult =
  | { status: "success" }
  | { status: "totp_required";         cognitoUser: CognitoUser }
  | { status: "mfa_setup";             cognitoUser: CognitoUser; secretCode: string }
  | { status: "new_password_required"; cognitoUser: CognitoUser };

export { CognitoUser };

// ---- Auth operations ----

/**
 * Sign in with email + password.
 * - "success"               → tokens stored in localStorage; user is signed in.
 * - "totp_required"         → pass cognitoUser to respondToTotp().
 * - "mfa_setup"             → show the secretCode QR/text and pass cognitoUser to verifyTotpSetup().
 * - "new_password_required" → first sign-in from an invite (FORCE_CHANGE_PASSWORD);
 *                             pass cognitoUser + the chosen password to completeNewPassword().
 */
export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    const auth = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(auth, {
      onSuccess: (session) => { storeSession(session); resolve({ status: "success" }); },
      onFailure: (err)     => reject(err),
      totpRequired: ()     => resolve({ status: "totp_required", cognitoUser: user }),
      mfaSetup: ()         => {
        user.associateSoftwareToken({
          associateSecretCode: (secretCode) =>
            resolve({ status: "mfa_setup", cognitoUser: user, secretCode }),
          onFailure: (err) => reject(err),
        });
      },
      // Invited users start in FORCE_CHANGE_PASSWORD; Cognito challenges for a new
      // permanent password on first sign-in. Surface it so the UI can collect one.
      newPasswordRequired: () => resolve({ status: "new_password_required", cognitoUser: user }),
    });
  });
}

/**
 * Complete the new-password challenge for an invited user's first sign-in.
 * On success the result mirrors signIn(): usually "success", but if the pool
 * (or the user's preference) requires MFA, Cognito may follow up with an MFA
 * challenge — handled here so the caller can route to the same TOTP screens.
 */
export function completeNewPassword(cognitoUser: CognitoUser, newPassword: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    // Pass no additional required attributes — email is already set on the account.
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => { storeSession(session); resolve({ status: "success" }); },
      onFailure: (err)     => reject(err),
      totpRequired: ()     => resolve({ status: "totp_required", cognitoUser }),
      mfaSetup: ()         => {
        cognitoUser.associateSoftwareToken({
          associateSecretCode: (secretCode) =>
            resolve({ status: "mfa_setup", cognitoUser, secretCode }),
          onFailure: (err) => reject(err),
        });
      },
    });
  });
}

/** Respond to a TOTP challenge (user already has TOTP configured). */
export function respondToTotp(cognitoUser: CognitoUser, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(
      code,
      {
        onSuccess: (session) => { storeSession(session); resolve(); },
        onFailure: (err)     => reject(err),
      },
      "SOFTWARE_TOKEN_MFA",
    );
  });
}

/**
 * Verify a TOTP code during initial MFA setup, then enable TOTP as the
 * preferred MFA method for this user account.
 */
export function verifyTotpSetup(cognitoUser: CognitoUser, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(code, "Authenticator", {
      onSuccess: (session) => {
        cognitoUser.setUserMfaPreference(
          null,
          { Enabled: true, PreferredMfa: true },
          (err) => { if (err) reject(err); else { storeSession(session); resolve(); } },
        );
      },
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Start the forgot-password flow: Cognito emails a verification code to the
 * account's address. Resolves even if the address doesn't exist (Cognito's
 * own behavior here already avoids confirming account existence).
 */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Complete the forgot-password flow with the emailed code and a new password. */
export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Sign out: clears stored tokens. */
export function signOut(): void {
  [SK_ACCESS, SK_ID, SK_REFRESH, SK_EXPIRES_AT, "medassist.token"].forEach(
    (k) => localStorage.removeItem(k),
  );
}

/** Alias so existing callers of revokeToken() continue to work unchanged. */
export const revokeToken = signOut;

/**
 * Refresh the current session using the stored refresh token.
 * Returns true on success (new tokens written to localStorage), false otherwise.
 * Used by api.ts on 401 responses.
 */
export async function refreshTokens(): Promise<boolean> {
  if (!isCognitoConfigured()) return false;
  const storedRefresh = localStorage.getItem(SK_REFRESH);
  const storedId      = localStorage.getItem(SK_ID);
  if (!storedRefresh || !storedId) return false;

  // Extract username (email preferred, sub as fallback) from the stored ID token.
  const payload  = decodeJwtPayload(storedId);
  const username = (payload["email"] ?? payload["sub"]) as string | undefined;
  if (!username) return false;

  return new Promise((resolve) => {
    const user  = new CognitoUser({ Username: username, Pool: getPool() });
    const token = new CognitoRefreshToken({ RefreshToken: storedRefresh });
    user.refreshSession(token, (err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) { resolve(false); return; }
      storeSession(session);
      resolve(true);
    });
  });
}

